import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useSession } from "@/lib/auth-client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Plus,
  Edit,
  ExternalLink,
  Trash2,
  MoreVertical,
  Link2,
  Settings,
  BadgeCheckIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDashboardOverview, deleteBio, createBioDraft } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LinkTransferInitiateDialog } from "@/components/editor/LinkTransferInitiateDialog";
import { LinkTransferClaimDialog } from "@/components/editor/LinkTransferClaimDialog";

export const Route = createFileRoute("/dashboard")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
}

interface PublicProfileData {
  id: string;
  username: string; // raw username or display
  name?: string | null;
  image?: string | null;
  displayUsername?: string | null;
  tagline?: string | null;
  location?: string | null;
  website?: string | null;
  followersCount?: number | null;
  followingCount?: number | null;
  createdAt: string;
  updatedAt: string;
  verified?: boolean | null;
  isSiteAdmin?: boolean | null;
}

function DashboardContent() {
  const { data: session } = useSession();

  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    slug: string;
  } | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState<
    "updatedAt" | "viewsCount" | "createdAt" | "username"
  >("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: [
      "dashboard-overview",
      { page, pageSize, sortBy, sortOrder },
    ],
    queryFn: async () => {
      const res = await getDashboardOverview({
        page,
        pageSize,
        sortBy,
        sortOrder,
      });
      if (!res.ok) throw new Error("failed to load overview");
      return (await res.json()) as {
        me: PublicProfileData;
        bios: Array<{
          id: string;
          username: string;
          isPublished?: string;
          updatedAt?: string;
          viewsCount?: number;
          pendingTransfer?: boolean;
        }>;
        page?: {
          page: number;
          pageSize: number;
          total: number;
          totalPages: number;
          sortBy: "updatedAt" | "viewsCount" | "createdAt" | "username";
          sortOrder: "asc" | "desc";
        };
      };
    },
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const data = q.state.data as any;
      if (!data?.bios) return false;
      const hasPending = data.bios.some((b: any) => b?.pendingTransfer);
      return hasPending ? 15000 : false;
    },
  });

  const profileData = overview?.me || null;
  const bios = overview?.bios;

  // keep local page in bounds when data total changes
  useEffect(() => {
    const meta = (overview as any)?.page;
    if (meta && typeof meta.totalPages === "number") {
      if (page > meta.totalPages) setPage(Math.max(1, meta.totalPages));
    }
  }, [overview, page]);

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteBio(id);
      if (!res.ok) throw new Error("failed to delete");
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-overview"] });
      toast.success("link deleted");
    },
    onError: () => {
      toast.error("failed to delete link");
    },
  });

  const rel = (iso?: string) => {
    if (!iso) return "unknown";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  };

  const claimedLinks = useMemo((): Array<{
    id: string;
    slug: string;
    displaySlug: string;
    isUnclaimed: boolean;
    title: string;
    views: number;
    lastEdited?: string;
    status: "active" | "draft";
    pendingTransfer?: boolean;
  }> => {
    return (bios ?? []).map((b) => {
      const isUnclaimed = !b.username || b.username === "new";
      return {
        id: b.id,
        slug: b.username,
        displaySlug: isUnclaimed ? "" : b.username,
        isUnclaimed,
        title: isUnclaimed ? "untitled" : b.username,
        views:
          typeof (b as any).viewsCount === "number" ? (b as any).viewsCount : 0,
        lastEdited: rel(b.updatedAt),
        status: b.isPublished === "true" ? "active" : ("draft" as const),
        pendingTransfer: (b as any).pendingTransfer === true,
      };
    });
  }, [bios]);

  const [showClaim, setShowClaim] = useState(false);
  const [showInitiate, setShowInitiate] = useState<null | {
    id: string;
    slug: string;
  }>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  const handleCreateDraft = async () => {
    if (creatingDraft) return;
    setCreatingDraft(true);
    try {
      const res = await createBioDraft({});
      if (res.status === 401) {
        window.location.href = "/sign-in";
        return;
      }
      if (!res.ok) {
        toast.error("failed to create draft");
        return;
      }
      const data = await res.json();
      const newId: string | undefined = data?.id;
      if (!newId) {
        toast.error("draft id missing");
        return;
      }
      // navigate to editor so dashboard refreshes on return
      window.location.href = `/editor/${encodeURIComponent(newId)}`;
    } catch {
      toast.error("failed to create draft");
    } finally {
      setCreatingDraft(false);
    }
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-20 text-amber-400/30 text-2xl animate-pulse">
          ?
        </div>
        <div
          className="absolute top-40 right-32 text-amber-500/20 text-xl animate-bounce"
          style={{ animationDelay: "0.5s" }}
        >
          ?
        </div>
        <div
          className="absolute bottom-32 left-40 text-orange-400/25 text-lg animate-pulse"
          style={{ animationDelay: "1s" }}
        >
          ?
        </div>
        <div
          className="absolute top-60 left-1/2 text-yellow-400/20 text-xl animate-bounce"
          style={{ animationDelay: "1.5s" }}
        >
          ?
        </div>
        <div
          className="absolute bottom-40 right-20 text-amber-400/30 text-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        >
          ?
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">dashboard</h1>
            <p className="text-gray-600 mt-1">manage your clove.moe links and profile</p>
          </div>
          <div className="flex items-center gap-2">
            {(profileData as any)?.isSiteAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => (window.location.href = "/admin")}
              >
                <ShieldCheckIcon className="h-4 w-4" />
                admin
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => (window.location.href = "/settings")}
            >
              <Settings className="h-4 w-4" />
              settings
            </Button>
          </div>
        </div>

        <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-amber-200">
                <AvatarImage src={session.user.image || undefined} />
                <AvatarFallback className="bg-amber-100 text-amber-800 text-lg font-semibold">
                  {session.user.name?.charAt(0).toUpperCase() ||
                    session.user.email.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {session.user.name || session.user.username || "user"}
                  </h2>
                  {(profileData as any)?.verified && (
                    <span title="Verified on clove.moe">
                      <BadgeCheckIcon className="w-5 h-5 text-accent" />
                    </span>
                  )}
                  {(profileData as any)?.isSiteAdmin && (
                    <span title="Site Administrator">
                      <ShieldCheckIcon className="w-5 h-5 text-primary" />
                    </span>
                  )}
                </div>
                <p className="text-gray-600">{session.user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge
                    variant="secondary"
                    className="bg-amber-100 text-amber-800 border-amber-200"
                  >
                    {(overview as any)?.page?.total ?? claimedLinks.length}/50 links claimed
                  </Badge>
                  <Badge
                    variant="outline"
                    className="border-green-200 text-green-700"
                  >
                    verified
                  </Badge>
                </div>
              </div>
              <div className="ml-auto self-start">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl hover:bg-amber-100 text-amber-700"
                  onClick={() => {
                    const slug =
                      (session.user as any)?.displayUsername ||
                      session.user.username ||
                      session.user.email.split("@")[0];
                    window.open(
                      `/profile/${encodeURIComponent(slug)}`,
                      "_blank"
                    );
                  }}
                  title="go to profile"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900">your links</h2>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-2xl">
                    sort: {sortBy === "updatedAt" && sortOrder === "desc" && "last edited"}
                    {sortBy === "viewsCount" && sortOrder === "desc" && "most viewed"}
                    {sortBy === "viewsCount" && sortOrder === "asc" && "least viewed"}
                    {sortBy === "createdAt" && sortOrder === "desc" && "newest"}
                    {sortBy === "createdAt" && sortOrder === "asc" && "oldest"}
                    {sortBy === "username" && sortOrder === "asc" && "a-z"}
                    {sortBy === "username" && sortOrder === "desc" && "z-a"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setSortBy("updatedAt"); setSortOrder("desc"); setPage(1); }}>
                    last edited
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("viewsCount"); setSortOrder("desc"); setPage(1); }}>
                    most viewed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("viewsCount"); setSortOrder("asc"); setPage(1); }}>
                    least viewed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("createdAt"); setSortOrder("desc"); setPage(1); }}>
                    newest
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("createdAt"); setSortOrder("asc"); setPage(1); }}>
                    oldest
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("username"); setSortOrder("asc"); setPage(1); }}>
                    a-z
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("username"); setSortOrder("desc"); setPage(1); }}>
                    z-a
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                className="rounded-2xl"
                onClick={() => setShowClaim(true)}
              >
                transfer a link
              </Button>
              <Button
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl disabled:opacity-60"
                onClick={handleCreateDraft}
                disabled={creatingDraft}
              >
                <Plus className="h-4 w-4" />
                {creatingDraft ? "creating..." : "create new link"}
              </Button>
            </div>
          </div>

          {overviewLoading ? (
            <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="h-6 w-40 bg-amber-100/60 rounded animate-pulse mb-2" />
                <div className="h-4 w-64 bg-amber-100/60 rounded animate-pulse" />
              </CardContent>
            </Card>
          ) : ((overview as any)?.page?.total ?? claimedLinks.length) === 0 ? (
            <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Link2 className="h-12 w-12 text-amber-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">no links yet</h3>
                <p className="text-gray-600 text-center mb-6 max-w-md">
                  start by creating your first clove.moe link to share your content with the world
                </p>
                <Button
                  className="gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl disabled:opacity-60"
                  onClick={handleCreateDraft}
                  disabled={creatingDraft}
                >
                  <Plus className="h-4 w-4" />
                  {creatingDraft ? "creating..." : "create your first link"}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {claimedLinks.map((link) => (
                <Card
                  key={link.id}
                  className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{link.title}</h3>
                          <Badge
                            variant={link.status === "active" ? "default" : "secondary"}
                            className={
                              link.status === "active"
                                ? "bg-green-100 text-green-800 border-green-200"
                                : "bg-gray-100 text-gray-600 border-gray-200"
                            }
                          >
                            {link.status}
                          </Badge>
                          {!link.isUnclaimed && link.pendingTransfer && (
                            <Badge
                              variant="outline"
                              className="border-amber-300 text-amber-700 bg-amber-100/60"
                              title="pending transfer in progress"
                            >
                              pending transfer
                            </Badge>
                          )}
                        </div>
                        {link.isUnclaimed ? (
                          <p className="text-gray-500 font-medium mb-2">not claimed yet</p>
                        ) : (
                          <p className="text-amber-600 font-medium mb-2">clove.moe/{link.displaySlug}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>{link.views.toLocaleString()} views</span>
                          <span>edited {link.lastEdited}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl hover:bg-amber-100"
                          disabled={link.isUnclaimed}
                          onClick={() => window.open(`/${link.slug}`, "_blank")}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl hover:bg-amber-100"
                          onClick={() => (window.location.href = `/editor/${link.slug}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl hover:bg-red-100 text-red-600"
                          disabled={deleteMutation.isPending}
                          onClick={() => setDeleteTarget({ id: link.id, slug: link.slug })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="rounded-xl hover:bg-amber-100">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={link.isUnclaimed}
                              onClick={() => setShowInitiate({ id: link.id, slug: link.slug })}
                            >
                              transfer ownership
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="flex items-center justify-between pt-2">
                <div className="text-sm text-gray-600">
                  {(() => {
                    const meta: any = (overview as any)?.page;
                    if (!meta) return null;
                    const start = (meta.page - 1) * meta.pageSize + 1;
                    const end = Math.min(meta.page * meta.pageSize, meta.total);
                    return `showing ${start}-${end} of ${meta.total}`;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    disabled={((overview as any)?.page?.page || 1) <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    previous
                  </Button>
                  <div className="text-sm text-gray-700">
                    page {(overview as any)?.page?.page || page} of {(overview as any)?.page?.totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    disabled={(() => {
                      const meta: any = (overview as any)?.page;
                      if (!meta) return true;
                      return meta.page >= meta.totalPages;
                    })()}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <LinkTransferClaimDialog
          open={showClaim}
          onOpenChange={setShowClaim}
          onClaimed={() => queryClient.invalidateQueries({ queryKey: ["bios"] })}
        />
        <LinkTransferInitiateDialog
          open={!!showInitiate}
          onOpenChange={(o) => (!o ? setShowInitiate(null) : void 0)}
          pageId={showInitiate?.id || ""}
          pageSlug={showInitiate?.slug || ""}
        />

        <Dialog
          open={!!deleteTarget}
          onOpenChange={(o) => {
            if (!o) setDeleteTarget(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>delete link</DialogTitle>
              <DialogDescription>
                {deleteTarget
                  ? deleteTarget.slug === "new"
                    ? "are you sure you want to delete this draft this cannot be undone"
                    : `are you sure you want to delete /${deleteTarget.slug}? this cannot be undone`
                  : ""}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
              >
                cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteMutation.isPending || !deleteTarget}
                onClick={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteMutation.mutateAsync(deleteTarget.id);
                    setDeleteTarget(null);
                  } catch {
                    // ignore error
                  }
                }}
              >
                {deleteMutation.isPending ? "deleting." : "delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

